// Line-count audit (2026-05-04): 549 lines.
import { useCallback } from 'react';
import { useEditorStore } from '@/stores/editor/editor-store';
import { usePlanStore } from '@/stores/ai/plan-store';
import { undoRedoService } from '@/services/ai/UndoRedoService';
import type { ComponentSnapshot as UndoRedoComponentSnapshot } from '@/services/ai/UndoRedoService';
import { skillsApi, type ProactiveSuggestion, type EditorContext } from '@/services/api/agentApi';
import * as agentConversationManager from '@/services/editor/AgentConversationManager';
import * as canvasSessionManager from '@/services/editor/CanvasSessionManager';
import { EditorContextSerializer } from '@/services/editor/EditorContextSerializer';
import { PostActionTracker } from '@/services/editor/PostActionTracker';
import type { Component } from '@/types';
import type { ComponentUpdatePayload } from '@/types/component-update';
import type { PropertyMessage } from '@/components/ai/types';
import type { UseSlashCommandsDeps, UseSlashCommandsReturn } from './agentMessagingTypes';
import { useSlashCommandCreation } from './useSlashCommandCreation';
import {
  getBlockedAICommandPermissionMessage,
  getMutationCategoriesForChanges,
  type AICommandMutationCategory,
} from '@/services/ai/agentCommandPermissions';
import {
  getSlashCommandMutationCategories,
  getSlashCommandMutationTargets,
} from './slashCommandPermission';

function appendMessage(previous: PropertyMessage[], message: PropertyMessage): PropertyMessage[] {
  return [...previous, message];
}

function restoreSnapshot(snapshot: UndoRedoComponentSnapshot): Partial<ComponentUpdatePayload> {
  const componentUpdate: Partial<ComponentUpdatePayload> = {};

  if (snapshot.style !== undefined) {
    componentUpdate.style = Object.fromEntries(Object.entries(snapshot.style));
  }
  if (snapshot.properties !== undefined) {
    componentUpdate.properties = snapshot.properties;
  }
  if (snapshot.responsivePosition !== undefined) {
    componentUpdate.responsivePosition = snapshot.responsivePosition;
  }
  if (snapshot.size !== undefined) {
    const width = typeof snapshot.size.width === 'number'
      ? snapshot.size.width
      : parseFloat(String(snapshot.size.width)) || 0;
    const height = typeof snapshot.size.height === 'number'
      ? snapshot.size.height
      : parseFloat(String(snapshot.size.height)) || 0;
    componentUpdate.size = { width, height };
  }

  return componentUpdate;
}

function getComponentForUndoRedoPermission(snapshot: UndoRedoComponentSnapshot): Component | null {
  return useEditorStore.getState().components.find((componentItem) => componentItem.id === snapshot.id)
    || null;
}

export function useSlashCommands(deps: UseSlashCommandsDeps): UseSlashCommandsReturn {
  const {
    component,
    multipleComponents,
    isCanvas,
    inputRef,
    isSlashCommand,
    executeSlashCommand,
    updateComponent,
    setShowAutocomplete,
    setMessages,
    setIsLoading,
    setInputValue,
    setError,
    persistMessage,
    generateMessageId,
    agentVisualThreadIdRef,
    streamingActionCountRef,
    pendingConfirmationActionsRef,
    getLastSuccessfulCommand,
    storePages,
    currentPageId,
    dataDiscovery,
    planOrchestrator,
    setProactiveSuggestions,
    applyPropertyChanges,
    buildStreamingCallbacks,
    commandPermissionGuard,
  } = deps;

  const { handleNaturalLanguageCreationCommand } = useSlashCommandCreation({
    component,
    multipleComponents,
    isCanvas,
    executeSlashCommand,
    setMessages,
    setInputValue,
    setIsLoading,
    setError,
    persistMessage,
    generateMessageId,
    applyPropertyChanges,
    commandPermissionGuard,
  });

  const handleSlashCommand = useCallback(
    async (userInput: string): Promise<{ handled: boolean }> => {
      if (isSlashCommand(userInput)) {
        setShowAutocomplete(false);

        const userMessage: PropertyMessage = {
          id: generateMessageId(),
          content: userInput,
          role: 'user',
          timestamp: new Date(),
        };
        setMessages((previous) => appendMessage(previous, userMessage));
        persistMessage(userMessage);
        setIsLoading(true);

        const targetComponent = component ?? (multipleComponents?.[0] ?? null);
        const lowerSlashCommand = userInput.toLowerCase();
        const slashCategories: AICommandMutationCategory[] =
          /^\/(?:add|insert|create|template|tpl|duplicate|dup|\+)\b/.test(lowerSlashCommand)
            ? ['insertion']
            : /^\/(?:delete|remove|rm)\b/.test(lowerSlashCommand)
              ? ['delete']
              : [];
        const slashPermission = commandPermissionGuard?.({
          categories: slashCategories,
          targetComponents: targetComponent ? [targetComponent] : [],
          isCanvasMutation: isCanvas,
        });

        if (slashPermission && !slashPermission.allowed) {
          const assistantMessage: PropertyMessage = {
            id: generateMessageId(),
            content: getBlockedAICommandPermissionMessage(slashPermission, 'content'),
            role: 'assistant',
            timestamp: new Date(),
            applied: false,
          };
          setMessages((previous) => appendMessage(previous, assistantMessage));
          persistMessage(assistantMessage);
          setInputValue('');
          setIsLoading(false);
          return { handled: true };
        }

        const skillMatch = userInput.match(/^\/sk(?:ill)?\s+(.+)$/i);
        if (skillMatch) {
          const skillName = skillMatch[1].trim();
          try {
            const searchResult = await skillsApi.search({ query: skillName, limit: 1 });
            const match = searchResult?.data?.[0];

            if (!match) {
              const noMatchMessage: PropertyMessage = {
                id: generateMessageId(),
                content: `No skill found matching "${skillName}". Open the Skills panel to browse available skills.`,
                role: 'assistant',
                timestamp: new Date(),
              };
              setMessages((previous) => appendMessage(previous, noMatchMessage));
              persistMessage(noMatchMessage);
            } else {
              const loadingMessage: PropertyMessage = {
                id: generateMessageId(),
                content: `Executing skill "${match.name}"...`,
                role: 'assistant',
                timestamp: new Date(),
                isStreaming: true,
              };
              setMessages((previous) => appendMessage(previous, loadingMessage));

              const editorContext: EditorContext = EditorContextSerializer.serialize();
              const threadId = agentVisualThreadIdRef.current || undefined;
              const sessionContext = threadId
                ? canvasSessionManager.getSessionContext(threadId)
                : undefined;

              streamingActionCountRef.current = 0;
              pendingConfirmationActionsRef.current = [];

              await agentConversationManager.routeToVisualEditorStreaming(
                `Execute skill "${match.name}": ${skillName}`,
                editorContext,
                buildStreamingCallbacks(loadingMessage, skillName),
                threadId,
                sessionContext,
                { isFollowUp: false, complexity: 'page-generation', mentionedBreakpoints: [] },
                { activeSkill: { skill_id: match.skill_id, name: match.name } },
              );

              const resolvedThreadId = agentVisualThreadIdRef.current;
              if (resolvedThreadId) {
                PostActionTracker.associateSkill(resolvedThreadId, match.skill_id);
              }
            }
          } catch (error: unknown) {
            const errorMessage: PropertyMessage = {
              id: generateMessageId(),
              content: `Failed to execute skill: ${error instanceof Error ? error.message : String(error)}`,
              role: 'assistant',
              timestamp: new Date(),
            };
            setMessages((previous) => appendMessage(previous, errorMessage));
            persistMessage(errorMessage);
          }

          setIsLoading(false);
          setInputValue('');
          return { handled: true };
        }

        try {
          const result = await executeSlashCommand(userInput, targetComponent, isCanvas);
          const resultCategories = getSlashCommandMutationCategories(result, {
            isCanvasMutation: isCanvas && !targetComponent,
          });
          const resultTargets = getSlashCommandMutationTargets(result, targetComponent);
          const resultPermission = commandPermissionGuard?.({
            categories: resultCategories,
            targetComponents: resultTargets.targetComponents,
            targetComponentIds: resultTargets.targetComponentIds,
            isCanvasMutation: isCanvas && !targetComponent,
          });

          if (resultPermission && !resultPermission.allowed) {
            const assistantMessage: PropertyMessage = {
              id: generateMessageId(),
              content: getBlockedAICommandPermissionMessage(resultPermission, 'content'),
              role: 'assistant',
              timestamp: new Date(),
              applied: false,
            };
            setMessages((previous) => appendMessage(previous, assistantMessage));
            persistMessage(assistantMessage);
            setInputValue('');
            setIsLoading(false);
            return { handled: true };
          }

          const assistantMessage: PropertyMessage = {
            id: generateMessageId(),
            content: result.message,
            role: 'assistant',
            timestamp: new Date(),
            propertyChanges: result.propertyChanges,
          };

          setMessages((previous) => appendMessage(previous, assistantMessage));
          persistMessage(assistantMessage);

          if (result.propertyChanges && targetComponent) {
            applyPropertyChanges(generateMessageId(), result.propertyChanges);
          }
          result.applyMutations?.();

          setInputValue('');

          setTimeout(() => {
            const currentValue = inputRef.current?.value;
            if (currentValue && currentValue.trim() !== '' && inputRef.current) {
              inputRef.current.value = '';
              setInputValue('');
            }
          }, 100);

          setIsLoading(false);
          return { handled: true };
        } catch {
          setError('Failed to execute command');
          setInputValue('');
          setIsLoading(false);
          return { handled: true };
        }
      }

      const lowerInput = userInput.toLowerCase().trim();
      const isUndoCommand = /^(undo|undo that|undo last|revert|go back)$/i.test(lowerInput);
      const isRedoCommand = /^(redo|redo that|redo last|apply again)$/i.test(lowerInput);

      if (isUndoCommand) {
        const userMessage: PropertyMessage = {
          id: generateMessageId(),
          content: userInput,
          role: 'user',
          timestamp: new Date(),
        };
        setMessages((previous) => appendMessage(previous, userMessage));
        persistMessage(userMessage);
        setInputValue('');

        const undoPreview = undoRedoService.peekUndo();
        if (undoPreview) {
          const { snapshot } = undoPreview;
          const restoredSnapshot = restoreSnapshot(snapshot);
          const targetComponent = getComponentForUndoRedoPermission(snapshot);
          const permissionResult = commandPermissionGuard?.({
            categories: getMutationCategoriesForChanges(restoredSnapshot),
            targetComponents: targetComponent ? [targetComponent] : [],
            targetComponentIds: [snapshot.id],
            isCanvasMutation: isCanvas,
          });

          if (permissionResult && !permissionResult.allowed) {
            const assistantMessage: PropertyMessage = {
              id: generateMessageId(),
              content: getBlockedAICommandPermissionMessage(permissionResult, 'content'),
              role: 'assistant',
              timestamp: new Date(),
              applied: false,
            };
            setMessages((previous) => appendMessage(previous, assistantMessage));
            persistMessage(assistantMessage);
            setIsLoading(false);
            return { handled: true };
          }

          const undoResult = undoRedoService.undo();
          if (!undoResult) {
            setIsLoading(false);
            return { handled: true };
          }

          const { action } = undoResult;
          updateComponent(snapshot.id, restoredSnapshot);

          const assistantMessage: PropertyMessage = {
            id: generateMessageId(),
            content: `↩️ Reverted: ${action.description}`,
            role: 'assistant',
            timestamp: new Date(),
            applied: true,
          };
          setMessages((previous) => appendMessage(previous, assistantMessage));
          persistMessage(assistantMessage);
        } else {
          const assistantMessage: PropertyMessage = {
            id: generateMessageId(),
            content: 'Nothing to undo. Make some changes first!',
            role: 'assistant',
            timestamp: new Date(),
          };
          setMessages((previous) => appendMessage(previous, assistantMessage));
          persistMessage(assistantMessage);
        }

        setIsLoading(false);
        return { handled: true };
      }

      if (isRedoCommand) {
        const userMessage: PropertyMessage = {
          id: generateMessageId(),
          content: userInput,
          role: 'user',
          timestamp: new Date(),
        };
        setMessages((previous) => appendMessage(previous, userMessage));
        persistMessage(userMessage);
        setInputValue('');

        const redoPreview = undoRedoService.peekRedo();
        if (redoPreview) {
          const { snapshot } = redoPreview;
          const restoredSnapshot = restoreSnapshot(snapshot);
          const targetComponent = getComponentForUndoRedoPermission(snapshot);
          const permissionResult = commandPermissionGuard?.({
            categories: getMutationCategoriesForChanges(restoredSnapshot),
            targetComponents: targetComponent ? [targetComponent] : [],
            targetComponentIds: [snapshot.id],
            isCanvasMutation: isCanvas,
          });

          if (permissionResult && !permissionResult.allowed) {
            const assistantMessage: PropertyMessage = {
              id: generateMessageId(),
              content: getBlockedAICommandPermissionMessage(permissionResult, 'content'),
              role: 'assistant',
              timestamp: new Date(),
              applied: false,
            };
            setMessages((previous) => appendMessage(previous, assistantMessage));
            persistMessage(assistantMessage);
            setIsLoading(false);
            return { handled: true };
          }

          const redoResult = undoRedoService.redo();
          if (!redoResult) {
            setIsLoading(false);
            return { handled: true };
          }

          const { action } = redoResult;
          updateComponent(snapshot.id, restoredSnapshot);

          const assistantMessage: PropertyMessage = {
            id: generateMessageId(),
            content: `↪️ Re-applied: ${action.description}`,
            role: 'assistant',
            timestamp: new Date(),
            applied: true,
          };
          setMessages((previous) => appendMessage(previous, assistantMessage));
          persistMessage(assistantMessage);
        } else {
          const assistantMessage: PropertyMessage = {
            id: generateMessageId(),
            content: 'Nothing to redo. Undo something first!',
            role: 'assistant',
            timestamp: new Date(),
          };
          setMessages((previous) => appendMessage(previous, assistantMessage));
          persistMessage(assistantMessage);
        }

        setIsLoading(false);
        return { handled: true };
      }

      const isRepeatCommand = /^(repeat|again|same|do it again|same thing)$/i.test(lowerInput);
      if (isRepeatCommand) {
        const userMessage: PropertyMessage = {
          id: generateMessageId(),
          content: userInput,
          role: 'user',
          timestamp: new Date(),
        };
        setMessages((previous) => appendMessage(previous, userMessage));
        persistMessage(userMessage);
        setInputValue('');

        const lastCommand = getLastSuccessfulCommand();
        if (lastCommand) {
          const replayMessage: PropertyMessage = {
            id: generateMessageId(),
            content: `Repeating: "${lastCommand.command}"...`,
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: true,
          };
          setMessages((previous) => appendMessage(previous, replayMessage));

          setTimeout(() => {
            setInputValue(lastCommand.command);
            setMessages((previous) => previous.map((message) =>
              message.id === replayMessage.id
                ? {
                    ...message,
                    content: `Type or modify the command above, then press Enter to apply.\nOriginal: "${lastCommand.command}"`,
                    isStreaming: false,
                  }
                : message
            ));
            setIsLoading(false);
          }, 100);
        } else {
          const assistantMessage: PropertyMessage = {
            id: generateMessageId(),
            content: 'No previous command to repeat. Try a command first!',
            role: 'assistant',
            timestamp: new Date(),
          };
          setMessages((previous) => appendMessage(previous, assistantMessage));
          persistMessage(assistantMessage);
        }

        setIsLoading(false);
        return { handled: true };
      }

      const pageNavMatch = userInput.match(/^(?:navigate|nav)\s+to\s+(?:page\s+)?(.+)$/i)
        || userInput.match(/^go\s*to\s+(?:page\s+)?(.+)$/i)
        || userInput.match(/^switch\s+to\s+page\s+(.+)$/i)
        || userInput.match(/^(?:open|show|view)\s+page\s+(.+)$/i);

      if (pageNavMatch && !component && !multipleComponents?.length) {
        const rawPageName = pageNavMatch[1].trim();
        const cleanPageName = rawPageName
          .replace(/^#/, '')
          .replace(/^["'\u201C\u201D\u2018\u2019]|["'\u201C\u201D\u2018\u2019]$/g, '')
          .trim();

        const userMessage: PropertyMessage = {
          id: generateMessageId(),
          content: userInput,
          role: 'user',
          timestamp: new Date(),
        };
        setMessages((previous) => appendMessage(previous, userMessage));
        persistMessage(userMessage);
        setInputValue('');

        let responseContent: string;
        if (!cleanPageName) {
          const availablePages = (storePages || [])
            .filter((page) => page.id !== currentPageId)
            .map((page) => page.name)
            .join(', ');
          responseContent = `Please specify a page name. Available pages: ${availablePages || 'none'}`;
        } else {
          const normalizedSearch = cleanPageName.toLowerCase().replace(/-/g, ' ');
          const normalizePageName = (name: string) => name.toLowerCase().replace(/-/g, ' ');
          const targetPage = (storePages || []).find(
            (page) => normalizePageName(page.name) === normalizedSearch
          ) || (storePages || []).find(
            (page) => normalizePageName(page.name).includes(normalizedSearch)
              || normalizedSearch.includes(normalizePageName(page.name))
          );

          if (!targetPage) {
            const availablePages = (storePages || [])
              .filter((page) => page.id !== currentPageId)
              .map((page) => page.name)
              .join(', ');
            responseContent = `Page "${cleanPageName}" not found. Available pages: ${availablePages || 'none'}`;
          } else if (targetPage.id === currentPageId) {
            responseContent = `Already on page "${targetPage.name}"`;
          } else {
            useEditorStore.getState().switchToPage(targetPage.id);
            responseContent = `Navigated to page "${targetPage.name}"`;
          }
        }

        const assistantMessage: PropertyMessage = {
          id: generateMessageId(),
          content: responseContent,
          role: 'assistant',
          timestamp: new Date(),
        };
        setMessages((previous) => appendMessage(previous, assistantMessage));
        persistMessage(assistantMessage);
        setIsLoading(false);
        return { handled: true };
      }

      if (agentConversationManager.isAccessibilityRequest(userInput)) {
        const userMessage: PropertyMessage = {
          id: generateMessageId(),
          content: userInput,
          role: 'user',
          timestamp: new Date(),
        };
        setMessages((previous) => appendMessage(previous, userMessage));
        persistMessage(userMessage);
        setInputValue('');
        setIsLoading(true);

        try {
          const editorContext: EditorContext = EditorContextSerializer.serialize();
          const auditResult = await agentConversationManager.routeToAccessibilityAudit(editorContext);

          let responseContent = 'Unable to perform accessibility audit.';
          if (auditResult) {
            const violationSummary = auditResult.violations.length > 0
              ? auditResult.violations.slice(0, 5).map((violation) =>
                  `- **${violation.rule}** (${violation.wcagCriteria}): ${violation.description} [${violation.severity}]`
                ).join('\n')
              : 'No violations found.';

            responseContent = `**Accessibility Score: ${auditResult.score}/100**\n\n${auditResult.summary}\n\n${violationSummary}`;

            const fixSuggestions: ProactiveSuggestion[] = auditResult.violations
              .filter((violation) => violation.fixActions && violation.fixActions.length > 0)
              .map((violation) => ({
                id: `a11y-fix-${violation.componentId}-${violation.rule}`,
                category: 'accessibility',
                severity: violation.severity === 'critical'
                  ? 'critical'
                  : violation.severity === 'serious'
                    ? 'warning'
                    : 'info',
                title: `Fix: ${violation.rule}`,
                description: violation.description,
                confidence: 0.9,
                suggestedActions: violation.fixActions,
                affectedComponentIds: [violation.componentId],
                wcagCriteria: violation.wcagCriteria,
              }));

            if (fixSuggestions.length > 0) {
              setProactiveSuggestions(fixSuggestions);
            }
          }

          const assistantMessage: PropertyMessage = {
            id: generateMessageId(),
            content: responseContent,
            role: 'assistant',
            timestamp: new Date(),
          };
          setMessages((previous) => appendMessage(previous, assistantMessage));
          persistMessage(assistantMessage);
        } catch {
          const errorMessage: PropertyMessage = {
            id: generateMessageId(),
            content: 'Failed to run accessibility audit. Please try again.',
            role: 'assistant',
            timestamp: new Date(),
          };
          setMessages((previous) => appendMessage(previous, errorMessage));
        }

        setIsLoading(false);
        return { handled: true };
      }

      const isDataDiscoveryQuery = /\b(what data (do i have|is available|can i use)|show\s+(me\s+)?(available\s+)?(data|sources|schemas)|browse\s+data|explore\s+data(\s+sources)?|my data|data sources)\b/i.test(lowerInput);
      if (isDataDiscoveryQuery) {
        const userMessage: PropertyMessage = {
          id: generateMessageId(),
          content: userInput,
          role: 'user',
          timestamp: new Date(),
        };
        setMessages((previous) => appendMessage(previous, userMessage));
        persistMessage(userMessage);
        setInputValue('');
        dataDiscovery.openModal();
        return { handled: true };
      }

      // Visual editor style/layout commands must never route to plan mode
      const VISUAL_EDITOR_CMD = /\b(colors?|backgrounds?|fonts?|styles?|themes?|borders?|shadows?|radii|radius|paddings?|margins?|opacit\w+|bold|italic|align\w*|spacing|fills?|strokes?|width|height|size)\b/i;
      const isVisualEditorCmd = VISUAL_EDITOR_CMD.test(userInput);

      const hasSelectedComponentContext = Boolean(component || (multipleComponents && multipleComponents.length > 0));
      const planState = usePlanStore.getState();
      if (!isVisualEditorCmd && !hasSelectedComponentContext && !planState.isActive && !planState.isExecuting) {
        const autoActivated = await planOrchestrator.tryAutoActivate(userInput);
        if (autoActivated) {
          return { handled: true };
        }
      }

      const nextPlanState = usePlanStore.getState();
      if (nextPlanState.isActive && !nextPlanState.isExecuting) {
        const inQuickMode = !nextPlanState.pipelineMode || nextPlanState.pipelineMode === 'quick';
        if (inQuickMode && isVisualEditorCmd) {
          usePlanStore.getState().deactivate();
          return { handled: false };
        }
        await planOrchestrator.handlePlanMessage(userInput);
        return { handled: true };
      }

      return { handled: false };
    },
    [
      component,
      multipleComponents,
      isCanvas,
      inputRef,
      isSlashCommand,
      executeSlashCommand,
      updateComponent,
      setShowAutocomplete,
      setMessages,
      setIsLoading,
      setInputValue,
      setError,
      persistMessage,
      generateMessageId,
      agentVisualThreadIdRef,
      streamingActionCountRef,
      pendingConfirmationActionsRef,
      getLastSuccessfulCommand,
      storePages,
      currentPageId,
      dataDiscovery,
      planOrchestrator,
      setProactiveSuggestions,
      applyPropertyChanges,
      buildStreamingCallbacks,
      commandPermissionGuard,
    ]
  );

  return {
    handleSlashCommand,
    handleNaturalLanguageCreationCommand,
  };
}
